/* eslint-disable tree-shaking/no-side-effects-in-initialization -- webpack entry ponints are allowed to be side effectful */

/**
 * @file A very minimal entrypoint for embeds.
 * This allows embeds to initially load very quickly before then
 * coordinating with each other to load the heavyweight full application
 * in a staggered fashion using `embedStaggerWorker.ts`.
 */

import "./embedEntry.scss";
// eslint-disable-next-line no-restricted-imports -- this is a special case where we don't want to use logging.ts
import { datadogLogs } from "@datadog/browser-logs";
import { retry } from "@hex/common";

import type {
  EmbedStaggerReq,
  EmbedStaggerRes,
} from "./embedStaggerWorkerTypes.js";
import { analyticsEnabled } from "./util/data.js";
import { setOnEmbedLoadedCallback } from "./util/embedLoaded.js";
import { initDatadogLogging } from "./util/initDatadog.js";

// copied from @hex/common so we don't need an extra import in this file
function guardNever(_value: never, safeVariant: unknown | undefined): Error {
  return new Error(`Unhandled discriminated union member: ${safeVariant}`);
}

if (analyticsEnabled) {
  try {
    initDatadogLogging();
  } catch (e) {
    console.error("Error initializing Datadog", e);
  }
}

let loadingStarted = false;
function loadApp(): void {
  if (loadingStarted) return;
  loadingStarted = true;

  void retry({
    func: () => import("./index"),
    retryCount: 5,
    interval: 150,
  });
}

const root = document.getElementById("root");
if (root != null) {
  // WARNING!! Setting innerHTML directly is dangerous because it can lead to XSS vulns.
  // Do NOT interpolate any variables in this string.
  // What we're doing is only okay because it is a static string.
  root.innerHTML = `
    <div class="initial-spinner">
      <div class="initial-spinner-inner">
        <svg
          fill="none"
          height="40"
          viewBox="0 0 40 40"
          width="40"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clipRule="evenodd"
            d="M17 4C17 2.34315 18.3431 1 20 1C21.6569 1 23 2.34315 23 4C23 5.65685 21.6569 7 20 7C18.3431 7 17 5.65685 17 4ZM20 0C17.7909 0 16 1.79086 16 4C16 6.20914 17.7909 8 20 8C22.2091 8 24 6.20914 24 4C24 1.79086 22.2091 0 20 0ZM17 36C17 34.3431 18.3431 33 20 33C21.6569 33 23 34.3431 23 36C23 37.6569 21.6569 39 20 39C18.3431 39 17 37.6569 17 36ZM20 32C17.7909 32 16 33.7909 16 36C16 38.2091 17.7909 40 20 40C22.2091 40 24 38.2091 24 36C24 33.7909 22.2091 32 20 32ZM9 28C7.34315 28 6 29.3431 6 31C6 32.6569 7.34315 34 9 34C10.6569 34 12 32.6569 12 31C12 29.3431 10.6569 28 9 28ZM5 31C5 28.7909 6.79086 27 9 27C11.2091 27 13 28.7909 13 31C13 33.2091 11.2091 35 9 35C6.79086 35 5 33.2091 5 31ZM1 20C1 18.3431 2.34315 17 4 17C5.65685 17 7 18.3431 7 20C7 21.6569 5.65685 23 4 23C2.34315 23 1 21.6569 1 20ZM4 16C1.79086 16 0 17.7909 0 20C0 22.2091 1.79086 24 4 24C6.20914 24 8 22.2091 8 20C8 17.7909 6.20914 16 4 16ZM36 17C34.3431 17 33 18.3431 33 20C33 21.6569 34.3431 23 36 23C37.6569 23 39 21.6569 39 20C39 18.3431 37.6569 17 36 17ZM32 20C32 17.7909 33.7909 16 36 16C38.2091 16 40 17.7909 40 20C40 22.2091 38.2091 24 36 24C33.7909 24 32 22.2091 32 20ZM28 31C28 29.3431 29.3431 28 31 28C32.6569 28 34 29.3431 34 31C34 32.6569 32.6569 34 31 34C29.3431 34 28 32.6569 28 31ZM31 27C28.7909 27 27 28.7909 27 31C27 33.2091 28.7909 35 31 35C33.2091 35 35 33.2091 35 31C35 28.7909 33.2091 27 31 27ZM31 6C29.3431 6 28 7.34315 28 9C28 10.6569 29.3431 12 31 12C32.6569 12 34 10.6569 34 9C34 7.34315 32.6569 6 31 6ZM27 9C27 6.79086 28.7909 5 31 5C33.2091 5 35 6.79086 35 9C35 11.2091 33.2091 13 31 13C28.7909 13 27 11.2091 27 9ZM6 9C6 7.34315 7.34315 6 9 6C10.6569 6 12 7.34315 12 9C12 10.6569 10.6569 12 9 12C7.34315 12 6 10.6569 6 9ZM9 5C6.79086 5 5 6.79086 5 9C5 11.2091 6.79086 13 9 13C11.2091 13 13 11.2091 13 9C13 6.79086 11.2091 5 9 5Z"
            fill="currentColor"
            fillRule="evenodd"
          />
        </svg>
      </div>
    </div>
`;
}

// If the browser doesn't support shared workers, just load the app immediately
if (window.SharedWorker == null) {
  loadApp();
} else {
  // Otherwise, use embedStaggerWorker to coordinating loading across embeds
  try {
    const sendMsg = (msg: EmbedStaggerReq): void => {
      embedWorker.port.postMessage(msg);
    };

    const handleMsg = (e: MessageEvent): void => {
      const msg = e.data as EmbedStaggerRes;
      if (msg.type === "start_load_res") {
        startLoading();
      } else if (msg.type === "request_to_load_ack_res") {
        clearTimeout(noRespTimeout);
      } else {
        guardNever(msg, (msg as EmbedStaggerRes).type);
      }
    };

    const startLoading = (): void => {
      setOnEmbedLoadedCallback(() => {
        sendMsg({ type: "done_loading_req" });
        window.removeEventListener("beforeunload", handleUnload);
      });
      loadApp();
    };

    const handleUnload = (): void => {
      sendMsg({ type: "close_req" });
    };

    const embedWorker = new SharedWorker(
      new URL("./embedStaggerWorker", import.meta.url),
    );
    embedWorker.port.addEventListener("message", handleMsg);
    embedWorker.port.start();

    // For some reason, only in Chrome incognito windows,
    // when a Hex app is embedded in a non-Hex domain,
    // our messages to our SharedWorker just don't work.
    // No errors are thrown, they just silently disappear into the void.
    // This timeout ensures that if that does happen we load right away.
    // See https://linear.app/hex/issue/SUP-927/extremely-slow-load-time-for-embeds-in-incognito
    const noRespTimeoutMs = 2_000;
    const noRespTimeout = window.setTimeout(() => {
      if (loadingStarted) return;

      loadApp();

      const logMsg = "Shared worker did not respond, loading immediately";
      if (analyticsEnabled) {
        datadogLogs.logger.error(logMsg);
      } else {
        console.error(logMsg);
      }
    }, noRespTimeoutMs);

    sendMsg({ type: "request_to_load_req" });

    // This might not always work, but try to let the worker know if the tab is closed before loading ends
    window.addEventListener("beforeunload", handleUnload);

    // Set a backup plan just in case the SharedWorker has some issue:
    // if the embed hasn't loaded in 30s, load it sometime in the next 30s
    const forceLoadTimeoutMs = 30_000 + Math.random() * 30_000;
    window.setTimeout(() => {
      if (loadingStarted) return;

      loadApp();

      const logMsg = "Embed loading not triggered until timeout";
      if (analyticsEnabled) {
        datadogLogs.logger.error(logMsg);
      } else {
        console.error(logMsg);
      }
    }, forceLoadTimeoutMs);
  } catch (e: unknown) {
    const err = e instanceof Error ? e : new Error(JSON.stringify(e));
    const logMsg = "Error using SharedWorker to load embed";
    if (analyticsEnabled) {
      datadogLogs.logger.error(logMsg, undefined, err);
    } else {
      console.error(logMsg, err);
    }

    // After logging the error, just try to load the app directly,
    // better to at least attempt to load it then do nothing.
    loadApp();
  }
}
